import '@xyflow/react/dist/style.css'

import {
  addEdge,
  Background,
  Controls,
  type Edge,
  type Node,
  type NodeProps,
  type OnConnect,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGeckoNotifications } from 'features/notifications'
import { useRightDrawer } from 'features/rightDrawer'
import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import type { PfdNode, SvgTemplate } from 'types'
import { RolodexEntryPutJson, RolodexEntryType } from 'types'
import { v4 as uuidv4 } from 'uuid'

import { CustomSymbolNode } from './components/nodes/CustomSymbolNode'
import {
  registerCustomSymbol,
  type SymbolDefinition,
} from './data/MinimalSymbolLibrary'
import { SvgNode } from './SvgNode'

const nodeTypes = { svg: SvgNode } as const
const FlowPane = styled.div`
  flex: 1;
  min-height: 0;
  background: #111;
  display: flex;
  flex-direction: column;

  .react-flow__panel.top.right {
    color: ${({ theme }) => theme.forecast.activeText};
  }

  .react-flow__controls {
    background: ${({ theme }) => theme.forecast.background2};
    border: 1px solid ${({ theme }) => theme.forecast.background3};
    border-radius: 0.25rem;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.3);
    margin: 0.5rem;

    button {
      background: ${({ theme }) => theme.forecast.background2};
      border-bottom: 1px solid ${({ theme }) => theme.forecast.background3};
      color: ${({ theme }) => theme.forecast.activeText};

      &:hover {
        background: ${({ theme }) => theme.forecast.background3};
      }

      &:last-child {
        border-bottom: none;
      }

      svg {
        fill: currentColor;
      }
    }
  }
`

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.background3};
  background: ${({ theme }) => theme.forecast.background2};
`

const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
`

const FooterTitle = styled.div`
  font-weight: 600;
`

const UploadContainer = styled.div`
  margin-left: 0.5rem;
  flex: 0 1 17.5rem;
  max-width: 17.5rem;
  min-width: 7.5rem;
  overflow: hidden;
`

const FileInput = styled.input`
  width: 100%;
  min-width: 0;
`

const ActionsBar = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  white-space: nowrap;
`

const PreviewBox = styled.div`
  width: 2rem;
  height: 2rem;
`

const StyledReactFlow = styled(ReactFlow)`
  width: 100%;
  height: 100%;
`

const ScaleInput = styled.input`
  width: 5rem;
`

function buildNodes(
  tpl: SvgTemplate | null,
  scale: number,
  removeMode: boolean | undefined,
  onRemovePort: ((id: string) => void) | undefined,
  onAddPort: ((xPct: number, yPct: number) => void) | undefined
): Node[] {
  if (!tpl) return []
  const scaledWidth = tpl.viewBox.width * Math.max(0.01, scale || 1)
  const scaledHeight = tpl.viewBox.height * Math.max(0.01, scale || 1)
  return [
    {
      id: 'svg-1',
      type: 'svg',
      position: { x: 0, y: 0 },
      data: {
        svg: tpl.svg,
        width: scaledWidth,
        height: scaledHeight,
        ports: tpl.ports,
        removeMode,
        onRemovePort,
        onAddPort,
      },
      // Match SvgNode buffer (24px each side) so the dotted area is fully clickable.
      // Also add a small extra pixel to absorb border thickness and rounding.
      style: {
        width: scaledWidth + 49,
        height: scaledHeight + 49,
      },
      draggable: false,
      selectable: false,
    },
  ]
}

type SymbolImportProps = {
  siteId: string | undefined
}

const Inner: FC<SymbolImportProps> = ({ siteId }) => {
  const [tpl, setTpl] = useState<SvgTemplate | null>(null)
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const { fitView } = useReactFlow()
  const [removeMode, setRemoveMode] = useState(false)
  const [undoStack, setUndoStack] = useState<SvgTemplate[]>([])
  const [redoStack, setRedoStack] = useState<SvgTemplate[]>([])
  const { rolodexPut } = useRolodexEdit()
  const [scale, setScale] = useState<number>(1)

  const handleAddPort = useCallback(
    (xPct: number, yPct: number) => {
      setTpl((curr) => {
        if (!curr) return curr
        const id = `p-${Date.now()}`
        const ports = [
          ...curr.ports,
          { id, x: xPct, y: yPct, type: 'both' as const, angle: 0 as const },
        ]
        const next = { ...curr, ports }
        setUndoStack((s) => [...s, curr])
        setRedoStack([])
        return next
      })
    },
    [setTpl, setUndoStack, setRedoStack]
  )
  const { successNotification, errorNotification } = useGeckoNotifications()
  const { closeDrawer } = useRightDrawer()

  const buildHandleIdSet = useCallback((ports: SvgTemplate['ports']) => {
    const ids = new Set<string>()
    for (const p of ports) {
      if (p.type === 'source' || p.type === 'both') ids.add(`${p.id}-s`)
      if (p.type === 'target' || p.type === 'both') ids.add(`${p.id}-t`)
    }
    return ids
  }, [])

  const onRemovePort = useCallback(
    (id: string) => {
      setTpl((curr) => {
        if (!curr) return curr
        setUndoStack((s) => [...s, curr])
        setRedoStack([])
        const next = { ...curr, ports: curr.ports.filter((p) => p.id !== id) }
        const valid = buildHandleIdSet(next.ports)
        setEdges((eds) =>
          eds.filter(
            (e) =>
              (!e.sourceHandle || valid.has(e.sourceHandle)) &&
              (!e.targetHandle || valid.has(e.targetHandle))
          )
        )
        return next
      })
    },
    [buildHandleIdSet, setEdges]
  )

  const registerAsCustomNode = useCallback(async () => {
    if (!tpl) return
    const customId = `custom_${tpl.name.replace(/\W+/g, '_')}_${tpl.id}`
    // Sanitize svg to inherit theme color instead of hardcoding white
    const sanitizedSvg = (tpl.svg || '')
      .replace(/color:\s*#ffffff/gi, 'color: currentColor')
      .replace(/color:\s*#fff\b/gi, 'color: currentColor')
    const template = {
      svg: sanitizedSvg,
      viewBox: tpl.viewBox,
      // Persist only normalized ports; size is implicit from viewBox and runtime scale
      ports: tpl.ports.map((p) => ({ id: p.id, x: p.x, y: p.y })),
    }

    const CustomComponent = (
      props: React.ComponentProps<typeof CustomSymbolNode>
    ) => <CustomSymbolNode {...props} template={template} />

    const def: SymbolDefinition = {
      id: customId,
      name: tpl.name,
      description: 'Custom PFD node',
      category: 'custom',
      tags: ['custom', 'imported'],
      component: CustomComponent as unknown as React.ComponentType<
        NodeProps<PfdNode>
      >,
      previewComponent: () => (
        <PreviewBox dangerouslySetInnerHTML={{ __html: sanitizedSvg }} />
      ),
    }

    registerCustomSymbol(def)

    // Prepare payload for saving to Rolodex
    const payload: RolodexEntryPutJson = {
      id: customId,
      type: RolodexEntryType.CUSTOM_PFD_NODE,
      data: { name: tpl.name, template },
      tags: siteId ? { site: siteId } : {},
      index: {},
    }
    try {
      await rolodexPut([payload])
      successNotification('Custom node saved and registered.')
      closeDrawer()
    } catch (e) {
      // Surface detailed error if available
      // @ts-expect-error axios-like error shape
      const msg = e?.response?.data || e?.message || e
      console.error('Failed saving custom node', msg)
      errorNotification('Failed to save custom node')
    }
  }, [
    tpl,
    siteId,
    rolodexPut,
    successNotification,
    closeDrawer,
    errorNotification,
  ])

  const onFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      const isJpeg =
        file.type === 'image/jpeg' || /\.(jpe?g)$/i.test(file.name || '')

      if (isJpeg) {
        const dataUrl: string = await new Promise((resolve, reject) => {
          const fr = new FileReader()
          fr.onload = () => resolve(fr.result as string)
          fr.onerror = reject
          fr.readAsDataURL(file)
        })

        const dims = await new Promise<{ width: number; height: number }>(
          (resolve, reject) => {
            const img = new Image()
            img.onload = () =>
              resolve({ width: img.naturalWidth, height: img.naturalHeight })
            img.onerror = reject
            img.src = dataUrl
          }
        )

        const width = dims.width || 800
        const height = dims.height || 600

        // Wrap JPEG in an SVG <image> so existing SVG-based flow works unchanged
        const text = `<?xml version="1.0" encoding="UTF-8"?>\n<svg viewBox="0 0 ${width} ${height}" class="symbol-import-svg" style="width:100%;height:100%" preserveAspectRatio="none">\n<style>.symbol-import-svg{color:#ffffff}</style>\n<image x="0" y="0" width="100%" height="100%" preserveAspectRatio="none" href="${dataUrl}"/>\n</svg>`

        const next: SvgTemplate = {
          id: uuidv4(),
          name: file.name,
          svg: text,
          viewBox: { width, height },
          ports: [],
        }
        setTpl(next)
        setScale(1)
        setNodes(buildNodes(next, 1, removeMode, onRemovePort, handleAddPort))
        setEdges([])
        setUndoStack([])
        setRedoStack([])
        requestAnimationFrame(() => fitView({ padding: 0.1 }))
        return
      }

      // SVG path (original logic)
      let text = await file.text()

      // Remove hardcoded width/height attributes on ANY <svg> tags (root or nested)
      text = text
        .replace(/(<svg[^>]*?)\swidth=("[^"]*"|'[^']*'|[^\s>]+)/gi, '$1')
        .replace(/(<svg[^>]*?)\sheight=("[^"]*"|'[^']*'|[^\s>]+)/gi, '$1')

      const doc = new DOMParser().parseFromString(text, 'image/svg+xml')
      const vbAttr = doc.documentElement.getAttribute('viewBox')
      let width = 800
      let height = 600
      if (vbAttr) {
        const [, , w, h] = vbAttr.split(/\s+/).map(Number)
        if (Number.isFinite(w) && Number.isFinite(h)) {
          width = w
          height = h
        }
      } else {
        width =
          Number(doc.documentElement.getAttribute('width') || width) || width
        height =
          Number(doc.documentElement.getAttribute('height') || height) || height
      }

      // Ensure the embedded <svg> scales to the container and is scoped
      text = text.replace(/<svg([^>]*?)>/i, (_, attrs: string) => {
        let a = attrs
          .replace(/\swidth=("|')[^"']*("|')/i, '')
          .replace(/\sheight=("|')[^"']*("|')/i, '')
          .replace(/\sfill=("|')[^"']*("|')/i, '')
        if (/style=("|')/i.test(a)) {
          a = a.replace(
            /style=("|')(.*?)("|')/i,
            (_m: string, q1: string, s: string, q2: string) => {
              // Strip any existing width/height declarations, then force 100%
              const withoutSize = s
                .replace(/width\s*:\s*[^;]+;?/gi, '')
                .replace(/height\s*:\s*[^;]+;?/gi, '')
              const merged = `${withoutSize};width:100%;height:100%`
                .replace(/;;+/g, ';')
                .replace(/^;|;$/g, '')
              return `style=${q1}${merged}${q2}`
            }
          )
        } else {
          a += ' style="width:100%;height:100%"'
        }
        if (!/class=/.test(a)) a += ' class="symbol-import-svg"'
        if (/preserveAspectRatio=/.test(a)) {
          a = a.replace(
            /preserveAspectRatio=("|')[^"']*("|')/i,
            'preserveAspectRatio="none"'
          )
        } else {
          a += ' preserveAspectRatio="none"'
        }
        const injected =
          '<style>.symbol-import-svg{color:#ffffff}.symbol-import-svg *{fill:none;stroke:currentColor}.symbol-import-svg svg{width:100%;height:100%}</style>'
        return `<svg${a}>${injected}`
      })

      // Remove only full-canvas background rects so the grid stays visible
      text = text.replace(
        /<rect([^>]*?)\swidth=("|')100%\2([^>]*?)\sheight=("|')100%\4([^>]*)>/gi,
        '<rect$1$3$5 fill="none" stroke="none">'
      )

      const next: SvgTemplate = {
        id: uuidv4(),
        name: file.name,
        svg: text,
        viewBox: { width, height },
        ports: [],
      }
      setTpl(next)
      setScale(1)
      setNodes(buildNodes(next, 1, removeMode, onRemovePort, handleAddPort))
      setEdges([])
      setUndoStack([])
      setRedoStack([])
      requestAnimationFrame(() => fitView({ padding: 0.1 }))
    },
    [fitView, setEdges, setNodes, removeMode, onRemovePort, handleAddPort]
  )

  // pane-level click-to-add disabled in favor of precise in-node clicks

  const onConnect = useCallback<OnConnect>(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  )

  // ensures that when a node is added, the view is fit to the node
  useEffect(() => {
    if (nodes.length > 0) {
      try {
        fitView({ padding: 0.1 })
      } catch {
        // ignore
      }
    }
  }, [nodes.length, fitView])

  useEffect(() => {
    setNodes(buildNodes(tpl, scale, removeMode, onRemovePort, handleAddPort))
  }, [tpl, removeMode, onRemovePort, setNodes, scale, handleAddPort])

  // Auto-exit edit mode if there are no handles
  useEffect(() => {
    if (removeMode && (!tpl || tpl.ports.length === 0)) {
      setRemoveMode(false)
    }
  }, [removeMode, tpl])

  const handleUndo = useCallback(() => {
    if (!tpl || undoStack.length === 0) return
    const prev = undoStack[undoStack.length - 1]
    setUndoStack((s) => s.slice(0, -1))
    setRedoStack((s) => [...s, tpl])
    setTpl(prev)
    const valid = buildHandleIdSet(prev.ports)
    setEdges((eds) =>
      eds.filter(
        (e) =>
          (!e.sourceHandle || valid.has(e.sourceHandle)) &&
          (!e.targetHandle || valid.has(e.targetHandle))
      )
    )
  }, [tpl, undoStack, buildHandleIdSet, setEdges])

  const handleRedo = useCallback(() => {
    if (!tpl || redoStack.length === 0) return
    const next = redoStack[redoStack.length - 1]
    setRedoStack((s) => s.slice(0, -1))
    setUndoStack((s) => [...s, tpl])
    setTpl(next)
    const valid = buildHandleIdSet(next.ports)
    setEdges((eds) =>
      eds.filter(
        (e) =>
          (!e.sourceHandle || valid.has(e.sourceHandle)) &&
          (!e.targetHandle || valid.has(e.targetHandle))
      )
    )
  }, [tpl, redoStack, buildHandleIdSet, setEdges])

  return (
    <OuterContainer>
      <FooterBar>
        <FooterTitle>Import Node</FooterTitle>
        <UploadContainer>
          <FileInput
            type='file'
            accept='.svg,image/svg+xml,.jpg,.jpeg,image/jpeg'
            onChange={onFileChange}
          />
        </UploadContainer>
        <ActionsBar>
          <label htmlFor='scale-input'>Scale</label>
          <ScaleInput
            id='scale-input'
            type='number'
            min={0.1}
            max={10}
            step={0.1}
            value={scale}
            onChange={(e) => {
              const v = parseFloat(e.target.value)
              if (Number.isFinite(v)) setScale(Math.max(0.1, Math.min(10, v)))
            }}
          />
          <button
            onClick={() => setScale(1)}
            disabled={scale === 1}
            title='Reset scale to 1'>
            Reset
          </button>
          <button onClick={registerAsCustomNode} disabled={!tpl}>
            Save Custom Node
          </button>
          {/**
           * Handle type selection temporarily disabled; handles are typeless and bidirectional
           * <label>Handle type</label>
           * <select
           *   value={selectedType}
           *   onChange={(e) =>
           *     setSelectedType(e.target.value as 'source' | 'target' | 'both')
           *   }>
           *   <option value='both'>both</option>
           *   <option value='source'>source</option>
           *   <option value='target'>target</option>
           * </select>
           */}
          <button
            onClick={() => setRemoveMode((v) => !v)}
            disabled={!tpl || tpl.ports.length === 0}>
            {removeMode ? 'Exit Delete' : 'Delete Handles'}
          </button>
          <button onClick={handleUndo} disabled={undoStack.length === 0}>
            Undo
          </button>
          <button onClick={handleRedo} disabled={redoStack.length === 0}>
            Redo
          </button>
        </ActionsBar>
      </FooterBar>
      <FlowPane>
        <StyledReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodesDraggable={false}
          elementsSelectable={false}
          panOnDrag={false}
          onInit={() => {
            try {
              fitView({ padding: 0.1 })
            } catch {
              /* ignore */
            }
          }}
          proOptions={{
            hideAttribution: true,
          }}
          onPaneClick={undefined}
          nodeTypes={nodeTypes}
          fitView>
          <Background />
          <Controls position='bottom-left' />
          <Panel position='top-right' style={{ pointerEvents: 'none' }}>
            Click on the SVG to add handles. Use scale to resize.
          </Panel>
        </StyledReactFlow>
      </FlowPane>
    </OuterContainer>
  )
}

export const SymbolImport: FC<SymbolImportProps> = ({ siteId }) => {
  return (
    <ReactFlowProvider>
      <Inner siteId={siteId} />
    </ReactFlowProvider>
  )
}
